import React, { useEffect, useRef, useState } from 'react';
// Use webpack helper which auto-configures a module worker (self-hosted)
 // eslint-disable-next-line @typescript-eslint/no-var-requires
const pdfjs: any = require('pdfjs-dist/webpack');

type PdfViewerProps = {
  src: string;                // Absolute or relative URL to a PDF
  title?: string;
  className?: string;
  height?: string | number;   // e.g. '75vh'
  initialScale?: number;      // 0.5 - 3.0
  style?: React.CSSProperties;
};

const configureWorker = () => {
  // pdfjs-dist/webpack sets up a module worker automatically.
  // If the environment blocks workers, we fall back to non-worker rendering below.
};

const PdfViewer: React.FC<PdfViewerProps> = ({
  src,
  title = 'Document',
  className = '',
  height = 'auto',
  initialScale = 1.0,
  style = {},
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [doc, setDoc] = useState<any | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [scale, setScale] = useState(initialScale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageHeight, setPageHeight] = useState<number>(600); // Default height

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    configureWorker();

    const load = async () => {
      try {
        // Normal worker path (module worker configured by pdfjs-dist/webpack)
        let task = (pdfjs as any).getDocument({ url: src });
        let loaded = await task.promise;
        if (cancelled) return;
        setDoc(loaded);
        setNumPages(loaded.numPages || 0);
        setPage(1);
      } catch (e1: any) {
        // Retry without worker if the worker failed to initialize (mobile blockers, CSP, etc.)
        try {
          let task2 = (pdfjs as any).getDocument({ url: src, worker: null });
          let loaded2 = await task2.promise;
          if (cancelled) return;
          setDoc(loaded2);
          setNumPages(loaded2.numPages || 0);
          setPage(1);
        } catch (e2: any) {
          if (!cancelled) {
            setError('Failed to load PDF');
            // eslint-disable-next-line no-console
            console.error('PDF load error:', e2 || e1);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
      try {
        (doc as any)?.destroy?.();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      if (!doc || !canvasRef.current) return;
      try {
        const pdfPage = await doc.getPage(page);
        if (cancelled) return;
        const viewport = pdfPage.getViewport({ scale });
        const canvas = canvasRef.current!;
        const ctx = canvas.getContext('2d')!;
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        // Update the page height based on the current scale
        setPageHeight(viewport.height + 42); // Add toolbar height
        
        const renderTask = pdfPage.render({ canvasContext: ctx, viewport });
        await renderTask.promise;
      } catch (e) {
        if (!cancelled) {
          setError('Failed to render PDF page');
          // eslint-disable-next-line no-console
          console.error('PDF render error:', e);
        }
      }
    };
    render();
    return () => {
      cancelled = true;
    };
  }, [doc, page, scale]);

  const zoomIn = () => setScale((s) => Math.min(3, +(s + 0.1).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.1).toFixed(2)));
  const prevPage = () => setPage((p) => Math.max(1, p - 1));
  const nextPage = () => setPage((p) => Math.min(numPages || 1, p + 1));

  // Security: Prevent right-click context menu and keyboard shortcuts
  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Prevent Ctrl+S (save) and Ctrl+P (print)
    if (e.ctrlKey && (e.key === 's' || e.key === 'p')) {
      e.preventDefault();
      return false;
    }
    // Prevent F12 (developer tools)
    if (e.key === 'F12') {
      e.preventDefault();
      return false;
    }
  };

  return (
    <div
      className={`pdfjs-viewer ${className || ''}`}
      style={{
        width: '100%',
        height: height === 'auto' ? `${pageHeight}px` : height,
        borderRadius: 10,
        overflow: 'hidden',
        background: '#ffffff',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        ...style
      }}
      aria-label={title}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div
        className="pdfjs-toolbar"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          background: '#f8fafc',
          flexWrap: 'wrap',
          minHeight: '42px',
        }}
      >
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={prevPage}
            disabled={page <= 1}
            title="Previous page"
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#fff',
              cursor: page <= 1 ? 'not-allowed' : 'pointer',
              minWidth: '32px',
              fontSize: '14px',
            }}
          >
            ◀
          </button>
          <span style={{
            minWidth: 80,
            textAlign: 'center',
            fontSize: 12,
            color: '#334155',
            whiteSpace: 'nowrap',
          }}>
            Page {page} / {numPages || 0}
          </span>
          <button
            onClick={nextPage}
            disabled={page >= (numPages || 1)}
            title="Next page"
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#fff',
              cursor: page >= (numPages || 1) ? 'not-allowed' : 'pointer',
              minWidth: '32px',
              fontSize: '14px',
            }}
          >
            ▶
          </button>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <button
            onClick={zoomOut}
            title="Zoom out"
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#fff',
              minWidth: '32px',
              fontSize: '16px',
            }}
          >
            −
          </button>
          <span style={{
            width: 52,
            textAlign: 'center',
            fontSize: 12,
            color: '#334155',
            whiteSpace: 'nowrap',
          }}>
            {(scale * 100).toFixed(0)}%
          </span>
          <button
            onClick={zoomIn}
            title="Zoom in"
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              background: '#fff',
              minWidth: '32px',
              fontSize: '16px',
            }}
          >
            +
          </button>
        </div>
      </div>

      <div style={{
        width: '100%',
        height: `calc(100% - 42px)`,
        overflow: 'auto',
        display: 'grid',
        placeItems: 'center',
        WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
      }}>
        {loading && <div style={{ color: '#334155', fontSize: 12 }}>Loading PDF…</div>}
        {error && (
          <div style={{
            padding: 12,
            textAlign: 'center',
            color: '#ef4444',
            fontSize: 14,
          }}>
            Document unavailable
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{
            display: loading || error ? 'none' : 'block',
            background: '#fff',
            maxWidth: '100%',
            height: 'auto',
          }}
          onContextMenu={handleContextMenu}
        />
      </div>
    </div>
  );
};

export default PdfViewer;